import { useQuery } from '@tanstack/react-query';
import { KPI } from '../../../layout/KPI.jsx';
import { api } from '../../../shared/api.js';
import { formatIQD } from '../../../shared/format.js';
import { useInventoryValueReport, useLowStockReport } from '../hooks/useStock.js';

function startOfMonthISO() {
  const d = new Date();
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

function useTotalProducts() {
  return useQuery({
    queryKey: ['inventory', 'kpi', 'productsCount'],
    queryFn: async () => {
      const { data } = await api.get('/catalog/products', { params: { active: true, limit: 1 } });
      return data?.total ?? 0;
    },
    staleTime: 60_000
  });
}

function useMonthlyPurchases() {
  return useQuery({
    queryKey: ['inventory', 'kpi', 'monthlyPurchases'],
    queryFn: async () => {
      const { data } = await api.get('/purchases', {
        params: { status: 'posted', from: startOfMonthISO(), limit: 200 }
      });
      const items = data?.items || [];
      const total = items.reduce((s, x) => s + (Number(x.total) || 0), 0);
      return { count: items.length, total };
    },
    staleTime: 60_000
  });
}

export function InventoryKpi() {
  const totalProducts = useTotalProducts();
  const value = useInventoryValueReport();
  const lowStock = useLowStockReport();
  const monthly = useMonthlyPurchases();

  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
      <KPI
        tone="orange"
        icon="products"
        label="إجمالي المنتجات"
        value={totalProducts.data ?? '—'}
        delta="نشط"
      />
      <KPI
        tone="yellow"
        icon="reports"
        label="مخزون منخفض"
        value={Array.isArray(lowStock.data) ? lowStock.data.length : (lowStock.data?.count ?? '—')}
        delta="بحاجة للطلب"
        deltaDirection="down"
      />
      <KPI
        tone="cyan"
        icon="inventory"
        label="قيمة المخزون"
        value={formatIQD(value.data?.total ?? 0)}
        delta={value.data?.warehouses?.length ? `${value.data.warehouses.length} مخازن` : ''}
      />
      <KPI
        tone="green"
        icon="purchase"
        label="مشتريات الشهر"
        value={formatIQD(monthly.data?.total ?? 0)}
        delta={`${monthly.data?.count ?? 0} فاتورة`}
      />
    </div>
  );
}
